import React, { useCallback, useEffect, useRef, useState } from 'react';
import Icon from './Icon';
import useLockBodyScroll from '../../hooks/useLockBodyScroll';

/**
 * Full-screen reel viewer: one vertical clip at a time, with sound on by
 * default (the visitor opted in by tapping) and arrow-key paging between reels.
 */
export function ReelPlayer({ reels, index, onClose, onNavigate }) {
  const open = index !== null && index >= 0;
  const videoRef = useRef(null);
  const panelRef = useRef(null);
  const openerRef = useRef(null);
  const [muted, setMuted] = useState(false);
  const [playing, setPlaying] = useState(true);

  useLockBodyScroll(open);

  useEffect(() => {
    if (open) openerRef.current = document.activeElement;
    else if (openerRef.current instanceof HTMLElement) openerRef.current.focus();
  }, [open]);

  useEffect(() => {
    if (open && panelRef.current) panelRef.current.focus();
  }, [open, index]);

  const step = useCallback(
    (delta) => {
      if (!open) return;
      onNavigate((index + delta + reels.length) % reels.length);
    },
    [open, index, reels.length, onNavigate]
  );

  // Start each clip from the top when the viewer pages between reels.
  useEffect(() => {
    const video = videoRef.current;
    if (!open || !video) return;

    video.currentTime = 0;
    const attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') {
      attempt.catch(() => {
        // Some browsers refuse unmuted autoplay — fall back to muted.
        setMuted(true);
        video.muted = true;
        video.play().catch(() => setPlaying(false));
      });
    }
    setPlaying(true);
  }, [open, index]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') step(1);
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') step(-1);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose, step]);

  if (!open) return null;

  const reel = reels[index];

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play().catch(() => {});
      setPlaying(true);
    } else {
      video.pause();
      setPlaying(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-ink-950/96 p-4 animate-fade-in backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={`Reel: ${reel.title}`}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div ref={panelRef} tabIndex={-1} className="relative flex items-center gap-4 outline-none sm:gap-8">
        <button
          type="button"
          onClick={() => step(-1)}
          aria-label="Previous reel"
          className="hidden h-12 w-12 shrink-0 place-items-center rounded-full border border-canvas/20 text-canvas transition hover:border-champagne-300 hover:text-champagne-300 sm:grid"
        >
          <Icon name="chevronLeft" />
        </button>

        <div className="relative w-[min(92vw,26rem)] overflow-hidden rounded-lg bg-black shadow-lift">
          <video
            ref={videoRef}
            key={reel.src}
            src={reel.src}
            poster={reel.poster}
            className="aspect-[9/16] h-auto w-full object-cover"
            playsInline
            loop
            muted={muted}
            onClick={togglePlay}
          />

          {/* Caption + controls sit over a scrim so text stays legible. */}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent p-5 pt-16">
            <p className="font-display text-xl text-canvas">{reel.title}</p>
            <p className="mt-0.5 text-xs leading-relaxed text-canvas/60">{reel.caption}</p>
          </div>

          <div className="absolute right-3 top-3 flex flex-col gap-2">
            <button
              type="button"
              onClick={onClose}
              aria-label="Close reel"
              className="grid h-10 w-10 place-items-center rounded-full bg-black/55 text-canvas backdrop-blur transition hover:bg-canvas hover:text-ink-950"
            >
              <Icon name="close" size={18} />
            </button>
            <button
              type="button"
              onClick={() => {
                const video = videoRef.current;
                if (!video) return;
                video.muted = !muted;
                setMuted(!muted);
              }}
              aria-label={muted ? 'Unmute reel' : 'Mute reel'}
              className="grid h-10 w-10 place-items-center rounded-full bg-black/55 text-canvas backdrop-blur transition hover:bg-canvas hover:text-ink-950"
            >
              <Icon name={muted ? 'volumeOff' : 'volume'} size={18} />
            </button>
            <button
              type="button"
              onClick={togglePlay}
              aria-label={playing ? 'Pause reel' : 'Play reel'}
              className="grid h-10 w-10 place-items-center rounded-full bg-black/55 text-canvas backdrop-blur transition hover:bg-canvas hover:text-ink-950"
            >
              <Icon name={playing ? 'pause' : 'play'} size={18} />
            </button>
          </div>

          {/* Progress pips double as a reel picker. */}
          <div className="absolute left-3 right-16 top-4 flex gap-1">
            {reels.map((item, i) => (
              <button
                key={item.slug}
                type="button"
                aria-label={`Go to reel ${i + 1}`}
                onClick={() => onNavigate(i)}
                className={`h-0.5 flex-1 rounded-full transition ${
                  i === index ? 'bg-champagne-300' : 'bg-canvas/25 hover:bg-canvas/50'
                }`}
              />
            ))}
          </div>
        </div>

        <button
          type="button"
          onClick={() => step(1)}
          aria-label="Next reel"
          className="hidden h-12 w-12 shrink-0 place-items-center rounded-full border border-canvas/20 text-canvas transition hover:border-champagne-300 hover:text-champagne-300 sm:grid"
        >
          <Icon name="chevronRight" />
        </button>
      </div>
    </div>
  );
}

export default ReelPlayer;
